/**
 * Gallery management mixin
 * Provides gallery functionality for preset elements
 */
import { markRaw } from 'vue';
export default {
    data() {
        return {
            // Gallery modal state
            show_gallery_modal: false,
            active_gallery_tab: '',
            active_gallery_subtab: '',
            gallery_admin_mode: false, // Toggle between user/admin view
            
            // Gallery categories with subcategories (loaded from server)
            gallery_categories: {},
            
            // Gallery items loaded from server
            gallery_items: {},
            gallery_loading: false,
            
            // Gallery insertion options
            insert_to_global_panel: false,
            
            // Admin panel state (only visible in dev)
            show_admin_panel: false,
            admin_selected_category: '',
            admin_selected_subcategory: '',
            admin_element_name: '',
            admin_element_is_premium: true, // DEFAULT PREMIUM, DONT CHANGE THIS!
            is_dev_environment: false,
            
            // Drag & drop sortable instance
            gallery_sortable: null
        };
    },
    
    mounted() {
        // Sync insert_to_global_panel with preferences
        if (this.preferences && this.preferences.editor) {
            this.insert_to_global_panel = this.preferences.editor.insert_to_global_panel;
        }
    },
    
    computed: {
        // Get gallery items for the active tab and subtab as an array
        current_gallery_items() {
            const key = `${this.active_gallery_tab}_${this.active_gallery_subtab}`;
            if (!this.gallery_items[key]) {
                return [];
            }
            const items = this.gallery_items[key] || [];
            
            // ALWAYS sort by the manual order (order_index) to preserve admin-configured order
            // This ensures all users see the same manually-configured order
            return items.sort((a, b) => {
                // First, separate by premium status (free items first) - this is sacred
                if (a.is_premium !== b.is_premium) {
                    return a.is_premium ? 1 : -1;
                }
                
                // Within each group (free/premium), sort by manual order_index
                const orderA = parseInt(a.order_index) || 0;
                const orderB = parseInt(b.order_index) || 0;
                return orderA - orderB;
            });
        },
        
        // Get current subcategories for active tab
        current_subcategories() {
            if (!this.gallery_categories[this.active_gallery_tab]) {
                return [];
            }
            const category_data = this.gallery_categories[this.active_gallery_tab];
            return category_data.subcategories || [];
        },
        
        // Get available admin categories
        admin_available_categories() {
            return Object.keys(this.gallery_categories);
        },
        
        // Get available admin subcategories for selected category
        admin_available_subcategories() {
            if (!this.gallery_categories[this.admin_selected_category]) {
                return [];
            }
            const category_data = this.gallery_categories[this.admin_selected_category];
            return category_data.subcategories || [];
        },
        
        // Get current subcategory info (for special messages)
        current_subcategory_info() {
            if (!this.gallery_categories[this.active_gallery_tab]) {
                return null;
            }
            const category_data = this.gallery_categories[this.active_gallery_tab];
            const subcategory = category_data.subcategories?.find(sub => 
                sub.clean === this.active_gallery_subtab
            );
            return subcategory?.info || null;
        },
        
        // Check if current subcategory should use composite preview
        use_composite_preview() {
            const info = this.current_subcategory_info;
            return info && info.composite_preview === true;
        }
    },
    
    watch: {
        // Auto-update admin subcategory when admin category changes
        admin_selected_category(new_category) {
            const category_data = this.gallery_categories[new_category];
            if (category_data && category_data.subcategories && category_data.subcategories.length > 0) {
                this.admin_selected_subcategory = category_data.subcategories[0].clean;
            }
        },
        
        // Auto-update gallery categories when they are loaded
        gallery_categories(new_categories) {
            // Update admin defaults if current selections are invalid
            const category_names = Object.keys(new_categories);
            
            if (category_names.length > 0) {
                // Set admin category if current one is invalid
                if (!new_categories[this.admin_selected_category]) {
                    this.admin_selected_category = category_names[0];
                }
                
                // Set admin subcategory if current one is invalid
                const category_data = new_categories[this.admin_selected_category];
                if (category_data && category_data.subcategories && category_data.subcategories.length > 0) {
                    const valid_subcategory = category_data.subcategories.find(sub => sub.clean === this.admin_selected_subcategory);
                    if (!valid_subcategory) {
                        this.admin_selected_subcategory = category_data.subcategories[0].clean;
                    }
                }
            }
        },

        // Initialize drag & drop when admin mode changes
        gallery_admin_mode(new_value) {
            this.$nextTick(() => {
                if (new_value && this.is_dev_environment) {
                    this.init_gallery_sortable();
                } else {
                    this.destroy_gallery_sortable();
                }
            });
        },

        // Re-initialize sortable when gallery items change in admin mode
        current_gallery_items() {
            if (this.gallery_admin_mode && this.is_dev_environment) {
                this.$nextTick(() => {
                    this.init_gallery_sortable();
                });
            }
        }
    },
    
    methods: {
        /**
         * Convert Konva blend mode to CSS mix-blend-mode
         */
        konva_to_css_blend_mode(konva_mode) {
            const blend_mode_map = {
                'normal': 'normal',
                'source-over': 'normal',
                'source-in': 'normal', // No direct equivalent, fallback
                'source-out': 'normal', // No direct equivalent, fallback
                'source-atop': 'normal', // No direct equivalent, fallback
                'destination-over': 'normal', // Different behavior in CSS
                'destination-in': 'normal', // No direct equivalent, fallback
                'destination-out': 'normal', // No direct equivalent, fallback
                'destination-atop': 'normal', // No direct equivalent, fallback
                'lighter': 'lighter',
                'copy': 'normal',
                'xor': 'exclusion', // Closest equivalent
                'multiply': 'multiply',
                'screen': 'screen',
                'overlay': 'overlay',
                'darken': 'darken',
                'lighten': 'lighten',
                'color-dodge': 'color-dodge',
                'color-burn': 'color-burn',
                'hard-light': 'hard-light',
                'soft-light': 'soft-light',
                'difference': 'difference',
                'exclusion': 'exclusion',
                'hue': 'hue',
                'saturation': 'saturation',
                'color': 'color',
                'luminosity': 'luminosity'
            };
            
            return blend_mode_map[konva_mode] || 'normal';
        },

        /**
         * Add element to gallery (general function for any type/subtype)
         */
        async add_to_gallery(type, subtype = null, element_name = null) {
            // Check if we have selected objects (for admin panel) or selected object (for regular use)
            const objects_to_add = this.selected_objects && this.selected_objects.length > 0 
                ? this.selected_objects 
                : (this.selected_object ? [this.selected_object] : []);
            
            if (objects_to_add.length === 0) {
                this.show_notification_modal(this.get_t('gallery.no_object_selected'), 'warning');
                return;
            }

            // If no name provided, prompt user
            if (!element_name) {
                element_name = await this.prompt_for_element_name();
                if (!element_name) {
                    // User cancelled
                    return;
                }
            }

            try {
                let success_count = 0;
                
                for (const obj of objects_to_add) {
                    // Generate thumbnail for this object
                    const thumbnail = await this.generate_thumbnail_for_object(obj);
                    
                    // Create proper object data like document.js does
                    let object_data = {};
                    
                    if (obj.getAttr('object_type') === 'balloon') {
                        // For balloons, use the same structure as document.js
                        object_data = {
                            id: obj.id(),
                            x: obj.x(),
                            y: obj.y(),
                            rotation: obj.rotation(),
                            scaleX: obj.scaleX(),
                            scaleY: obj.scaleY(),
                            visible: obj.visible(),
                            opacity: obj.opacity(),
                            offsetX: obj.offsetX(),
                            offsetY: obj.offsetY(),
                            locked: obj.getAttr('locked'),
                            layer_name: obj.getAttr('layer_name'),
                            balloon_type: obj.getAttr('balloon_type'),
                            balloon_points: obj.getAttr('balloon_points'),
                            balloon_size: obj.getAttr('balloon_size'),
                            fill_opacity: obj.getAttr('fill_opacity'),
                            original_stroke_width: obj.getAttr('original_stroke_width'),
                            original_stroke_dash: obj.getAttr('original_stroke_dash'),
                            custom_text_position: obj.getAttr('custom_text_position'),
                            custom_text_box_x: obj.getAttr('custom_text_box_x'),
                            custom_text_box_y: obj.getAttr('custom_text_box_y'),
                            custom_text_box_width: obj.getAttr('custom_text_box_width'),
                            custom_text_box_height: obj.getAttr('custom_text_box_height'),
                            globalCompositeOperation: obj.globalCompositeOperation(),
                            object_type: 'balloon'
                        };
                        
                        // Add shape properties
                        if (obj.shape) {
                            object_data.shape_fill = obj.shape.fill();
                            object_data.shape_stroke = obj.shape.stroke();
                            object_data.shape_stroke_width = obj.shape.strokeWidth();
                            object_data.shape_stroke_dash = obj.shape.dash();
                        }
                        
                        // Add text data if exists
                        if (obj.text) {
                            object_data.text_content = obj.text.text();
                            object_data.text_font_size = obj.text.fontSize();
                            object_data.text_font_family = obj.text.fontFamily();
                            object_data.text_font_style = obj.text.fontStyle();
                            object_data.text_fill = obj.text.fill();
                            object_data.text_stroke = obj.text.stroke();
                            object_data.text_stroke_width = obj.text.strokeWidth();
                            object_data.text_align = obj.text.align();
                            object_data.text_line_height = obj.text.lineHeight();
                            object_data.text_padding = obj.text.padding();
                            object_data.text_x = obj.text.x();
                            object_data.text_y = obj.text.y();
                            object_data.text_offset_x = obj.text.offsetX();
                            object_data.text_offset_y = obj.text.offsetY();
                        }

                        // Handle multiple texts for merged balloons
                        if (obj.texts && Array.isArray(obj.texts)) {
                            object_data.texts = obj.texts.map(text_node => ({
                                text_content: text_node.text() || '',
                                font_size: text_node.fontSize() || 16,
                                font_family: text_node.fontFamily() || 'Arial',
                                font_style: text_node.fontStyle() || 'normal',
                                fill: text_node.fill() || '#000000',
                                stroke: text_node.stroke() || '',
                                stroke_width: text_node.strokeWidth() || 0,
                                align: text_node.align() || 'center',
                                line_height: text_node.lineHeight() || 1.2,
                                padding: text_node.padding() || 5,
                                x: text_node.x() || 0,
                                y: text_node.y() || 0,
                                offset_x: text_node.offsetX() || 0,
                                offset_y: text_node.offsetY() || 0,
                                width: text_node.width() || 100,
                                height: text_node.height() || 50,
                                custom_box_for_recenter: text_node.getAttr('custom_box_for_recenter'),
                                scale_x: text_node.scaleX() || 1,
                                scale_y: text_node.scaleY() || 1,
                                custom_text_position: text_node.getAttr('custom_text_position'),
                                custom_text_box_x: text_node.getAttr('custom_text_box_x'),
                                custom_text_box_y: text_node.getAttr('custom_text_box_y'),
                                custom_text_box_width: text_node.getAttr('custom_text_box_width'),
                                custom_text_box_height: text_node.getAttr('custom_text_box_height')
                            }));
                        }

                        // Add other balloon-specific attributes
                        object_data.merged_path_data = obj.getAttr('merged_path_data');
                        object_data.original_fill = obj.getAttr('original_fill');
                        object_data.original_stroke = obj.getAttr('original_stroke');
                        object_data.balloon_width = obj.getAttr('balloon_width');
                        object_data.balloon_height = obj.getAttr('balloon_height');
                        object_data.balloon_cx = obj.getAttr('balloon_cx');
                        object_data.balloon_cy = obj.getAttr('balloon_cy');
                    } 
                    
                    else {
                        // For other object types (images, shapes, etc.), preserve more properties
                        object_data = obj.toObject();
                        object_data.object_type = obj.getAttr('object_type') || obj.getClassName();
                        
                        // Additional properties for images
                        if (obj.getClassName() === 'Image') {
                            object_data.src = obj.getAttr('src');
                            object_data.image_data = obj.getAttr('image_data');
                            object_data.original_width = obj.getAttr('original_width');
                            object_data.original_height = obj.getAttr('original_height');
                        }
                        
                        // Common properties for all objects
                        object_data.id = obj.id();
                        object_data.x = obj.x();
                        object_data.y = obj.y();
                        object_data.rotation = obj.rotation();
                        object_data.scaleX = obj.scaleX();
                        object_data.scaleY = obj.scaleY();
                        object_data.visible = obj.visible();
                        object_data.opacity = obj.opacity();
                        object_data.draggable = obj.draggable();
                        object_data.globalCompositeOperation = obj.globalCompositeOperation();
                        object_data.layer_name = obj.getAttr('layer_name');
                    }
                    
                    // Create simplified gallery item
                    const gallery_item = {
                        name: element_name,
                        type: type,
                        subtype: subtype,
                        object_data: object_data, // Use proper structured data
                        thumbnail_base64: thumbnail,
                        is_premium: this.admin_element_is_premium,
                        created_at: new Date().toISOString()
                    };
                    
                    // Create simple wrapper for gallery items
                    const gallery_document = {
                        gallery_item: gallery_item
                    };
                    
                    const form_data = new FormData();
                    form_data.append('request', 'save_gallery_item');
                    form_data.append('type', type);
                    if (subtype) {
                        form_data.append('subtype', subtype);
                    }
                    form_data.append('mkc_data', JSON.stringify(gallery_document));
                    
                    const response = await axios.post('ajax.php', form_data, {
                        headers: {'Content-Type': 'multipart/form-data'}
                    });
                    
                    if (response.data.success) {
                        success_count++;
                    }
                }
                
                if (success_count > 0) {
                    const message = success_count === 1 
                        ? this.get_t('gallery.item_added')
                        : this.get_t('gallery.items_added').replace('{count}', success_count);
                    this.show_notification_modal(message, 'success');
                    
                    // Force reload gallery items for the current tab to show new additions
                    this.load_gallery_items(this.active_gallery_tab, this.active_gallery_subtab);
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error adding to gallery:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Prompt user for element name
         */
        async prompt_for_element_name() {
            return new Promise((resolve) => {
                const name = prompt(this.get_t('gallery.enter_element_name'), '');
                resolve(name ? name.trim() : null);
            });
        },
        
        /**
         * Generate 400x400px thumbnail from any object
         */
        async generate_thumbnail_for_object(object) {
            if (!object) return null;
            
            // Create temporary stage for thumbnail
            const temp_stage = new Konva.Stage({
                container: document.createElement('div'),
                width: 400,
                height: 400
            });
            
            const temp_layer = new Konva.Layer();
            temp_stage.add(temp_layer);
            
            // Clone object and reset position to get proper bounds
            const cloned_object = object.clone();
            cloned_object.position({ x: 0, y: 0 });
            
            // Get bounds after resetting position
            const bounds = cloned_object.getClientRect();
            
            // Scale to fit in 380x380 (leaving 10px margin) while maintaining aspect ratio
            const scale = Math.min(380 / bounds.width, 380 / bounds.height);
            cloned_object.scale({ x: scale, y: scale });
            
            // Center the object properly in 400x400 canvas
            const scaled_bounds = cloned_object.getClientRect();
            cloned_object.position({
                x: (400 - scaled_bounds.width) / 2 - scaled_bounds.x,
                y: (400 - scaled_bounds.height) / 2 - scaled_bounds.y
            });
            
            temp_layer.add(cloned_object);
            temp_layer.draw();
            
            // Generate base64 thumbnail
            const thumbnail_base64 = temp_stage.toDataURL({
                mimeType: 'image/png',
                quality: 0.8
            });
            
            // Cleanup
            temp_stage.destroy();
            
            return thumbnail_base64;
        },
        
        /**
         * Generate 400x400px thumbnail from selected object (backward compatibility)
         */
        async generate_thumbnail_400x400() {
            return this.generate_thumbnail_for_object(this.selected_object);
        },
        
        /**
         * Load gallery items for specific category/subcategory
         */
        async load_gallery_items(category, subcategory) {
            if (!category || !subcategory) {
                console.warn('Cannot load gallery items: missing category or subcategory');
                return;
            }
            
            this.gallery_loading = true;
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'get_gallery_items');
                form_data.append('category', category);
                form_data.append('subcategory', subcategory);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    // Create key for storing items (category_subcategory)
                    const key = `${category}_${subcategory}`;
                    
                    // Ensure reactivity by creating a new object reference
                    const updated_items = { ...this.gallery_items };
                    updated_items[key] = response.data.items || [];
                    this.gallery_items = updated_items;
                } else {
                    console.error('Error in gallery response:', response.data);
                }
                
            } catch (error) {
                console.error('Error loading gallery items:', error);
            } finally {
                this.gallery_loading = false;
            }
        },
        
        /**
         * Load gallery categories from server
         */
        async load_gallery_categories() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'get_gallery_categories');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    this.gallery_categories = response.data.categories;
                    
                    // Set default active tab and subtab based on available categories
                    const category_names = Object.keys(this.gallery_categories);
                    if (category_names.length > 0) {
                        // Use current active tab if it exists, otherwise use first available
                        if (!this.gallery_categories[this.active_gallery_tab]) {
                            this.active_gallery_tab = category_names[0];
                        }
                        
                        // Set default subtab for active category
                        const category_data = this.gallery_categories[this.active_gallery_tab];
                        if (category_data && category_data.subcategories && category_data.subcategories.length > 0) {
                            // Use current subtab if it exists, otherwise use first available
                            const valid_subtab = category_data.subcategories.find(sub => sub.clean === this.active_gallery_subtab);
                            if (!valid_subtab) {
                                this.active_gallery_subtab = category_data.subcategories[0].clean;
                            }
                        }
                    }
                } else {
                    console.error('Error loading gallery categories:', response.data);
                }
                
            } catch (error) {
                console.error('Error loading gallery categories:', error);
            }
        },
        
        /**
         * Generate unique IDs for object and all its children to avoid conflicts
         */
        generate_unique_ids_for_object(object) {
            if (!object) return;
            
            // Generate new unique ID for this object
            object.id('obj_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
            
            // If it's a group, recursively update all children
            if (object.getClassName() === 'Group' && object.getChildren) {
                object.getChildren().forEach(child => {
                    this.generate_unique_ids_for_object(child);
                });
            }
        },
        
        /**
         * Insert gallery item into canvas
         */
        async insert_gallery_item(item) {
            try {
                // Check premium permissions
                if (item.is_premium && !this.user_data.is_premium) {
                    this.show_premium_modal('gallery_item');
                    return;
                }
                
                // Get item_id - for gallery items loaded from server
                let item_id = item.id || item.filename;
                if (typeof item === 'string') {
                    item_id = item;
                }
                
                if (!item_id) {
                    console.error('No item ID found in:', item);
                    this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                    return;
                }
                
                // Load MKC document and extract konva_data
                const form_data = new FormData();
                form_data.append('request', 'load_gallery_item');
                form_data.append('item_id', item_id);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success && response.data.mkc_document) {
                    const { gallery_item } = response.data.mkc_document;
                    
                    if (gallery_item && gallery_item.object_data) {
                        // Determine target panel based on checkbox and last_selected_panel
                        let target_panel;
                        
                        if (this.insert_to_global_panel) {
                            // User wants global panel
                            target_panel = this.panels.find(panel => panel.getAttr('is_global'));
                        } else if (this.last_selected_panel && !this.last_selected_panel.getAttr('is_global')) {
                            // User wants last selected panel (if it's not global)
                            target_panel = this.last_selected_panel;
                        } else {
                            // Fallback to global if no valid last selected panel
                            target_panel = this.panels.find(panel => panel.getAttr('is_global'));
                        }
                        
                        if (!target_panel || !target_panel.content_group) {
                            console.error('No valid target panel found');
                            this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                            return;
                        }
                        
                        // PREMIUM VALIDATION: Check limits before inserting gallery items
                        if (gallery_item.object_data.object_type === 'balloon') {
                            // Check balloons per page limit
                            const all_balloons = [];
                            this.panels.forEach(panel => {
                                if (panel.content_group) {
                                    const balloons = panel.content_group.find(node => node.getAttr('object_type') === 'balloon');
                                    all_balloons.push(...balloons);
                                }
                            });
                            
                            const current_balloon_count = all_balloons.length;
                            if (!this.check_local_restriction('max_balloons_per_page', current_balloon_count)) {
                                return;
                            }
                            
                            // Check layers per panel limit for balloon
                            const current_layer_count = this.get_panel_layer_count(target_panel);
                            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                                return;
                            }
                        } else if (gallery_item.object_data.object_type === 'image' || gallery_item.object_data.className === 'Image') {
                            // Check layers per panel limit for image
                            const current_layer_count = this.get_panel_layer_count(target_panel);
                            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                                return;
                            }
                        } else {
                            // For other objects, check layers per panel limit
                            const current_layer_count = this.get_panel_layer_count(target_panel);
                            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                                return;
                            }
                        }
                        
                        let new_object;
                        
                        // Handle different object types
                        if (gallery_item.object_data.object_type === 'balloon') {
                            // Use recreate_balloon_from_data exactly like document.js does
                            new_object = await this.recreate_balloon_from_data(gallery_item.object_data, target_panel.content_group, true);
                            
                            // Generate new unique ID
                            new_object.id('balloon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
                            
                        } else if (gallery_item.object_data.object_type === 'image' || gallery_item.object_data.className === 'Image') {
                            // Create image from src_data (base64) like load_image_into_panel does
                            // Try multiple paths to find image data
                            const src_data = gallery_item.object_data.attrs?.src_data || 
                                            gallery_item.object_data.src_data || 
                                            gallery_item.object_data.attrs?.src || 
                                            gallery_item.object_data.src || 
                                            gallery_item.object_data.image_data;
                            
                            if (!src_data) {
                                console.error('No image data found. Available data:', gallery_item.object_data);
                                this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                                return;
                            }
                            
                            const image = new Image();
                            image.onload = () => {
                                // Calculate center of target panel
                                const bounds = this.calculate_polygon_bounds(target_panel.points);
                                const center_x = bounds.min_x + bounds.width / 2;
                                const center_y = bounds.min_y + bounds.height / 2;
                                
                                // Create Konva image like load_image_into_panel does
                                const konva_image_raw = new Konva.Image({
                                    image: image,
                                    x: center_x,
                                    y: center_y,
                                    offset: {
                                        x: image.width / 2,
                                        y: image.height / 2
                                    },
                                    scaleX: gallery_item.object_data.scaleX || gallery_item.object_data.attrs?.scaleX || 1,
                                    scaleY: gallery_item.object_data.scaleY || gallery_item.object_data.attrs?.scaleY || 1,
                                    rotation: gallery_item.object_data.rotation || gallery_item.object_data.attrs?.rotation || 0,
                                    opacity: gallery_item.object_data.opacity !== undefined ? gallery_item.object_data.opacity : (gallery_item.object_data.attrs?.opacity || 1),
                                    visible: gallery_item.object_data.visible !== undefined ? gallery_item.object_data.visible : (gallery_item.object_data.attrs?.visible !== undefined ? gallery_item.object_data.attrs.visible : true),
                                    draggable: this.active_tool === 'select',
                                    name: 'panel_image',
                                    id: 'image_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                                    listening: true,
                                    globalCompositeOperation: gallery_item.object_data.globalCompositeOperation || gallery_item.object_data.attrs?.globalCompositeOperation || 'source-over'
                                });
                                
                                const konva_image = markRaw(konva_image_raw);
                                
                                // Set attributes like load_image_into_panel does
                                konva_image.setAttr('src_data', src_data);
                                konva_image.setAttr('layer_name', gallery_item.object_data.attrs?.layer_name || this.get_next_layer_name(target_panel));
                                konva_image.setAttr('locked', gallery_item.object_data.attrs?.locked || false);
                                konva_image.setAttr('object_type', 'image');
                                
                                // Add to target panel
                                target_panel.content_group.add(konva_image);
                                
                                // Position, select and finish
                                target_panel.getLayer().draw();
                                
                                // Activate selection tool and select the object
                                this.change_tool('select', false);
                                this.select_object(konva_image);

                                this.hide_gallery();
                                this.push_to_history('insert_gallery_item');
                                this.show_notification_modal(this.get_t('gallery.item_inserted'), 'success', true);
                            };
                            
                            image.onerror = () => {
                                console.error('Failed to load image data');
                                this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                            };
                            
                            image.src = src_data;
                            return; // Exit early since image loading is async
                            
                        } else {
                            // For other objects, use Konva.Node.create as fallback
                            try {
                                new_object = Konva.Node.create(gallery_item.object_data);
                                this.generate_unique_ids_for_object(new_object);
                                target_panel.content_group.add(new_object);
                            } catch (error) {
                                console.error('Error creating object from object_data:', error);
                                this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                                return;
                            }
                        }
                        
                        // Handle positioning and selection for non-image objects
                        if (new_object) {
                            // Position object in center of panel using same logic as paste_copied_object
                            const bounds = this.calculate_polygon_bounds(target_panel.points);
                            const center_x = bounds.min_x + bounds.width / 2;
                            const center_y = bounds.min_y + bounds.height / 2;
                            new_object.position({
                                x: center_x,
                                y: center_y
                            });
                            
                            // Update canvas
                            target_panel.getLayer().draw();
                            
                            // Activate selection tool and select the object
                            this.change_tool('select', false);
                            this.select_object(new_object);

                            // Close gallery modal
                            this.hide_gallery();
                            
                            // Add to history
                            this.push_to_history('insert_gallery_item');
                            
                            this.show_notification_modal(this.get_t('gallery.item_inserted'), 'success', true);
                        }
                    } else {
                        console.error('No gallery_item or object_data found in response');
                        this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                    }
                } else {
                    console.error('Invalid response from server:', response.data);
                    this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
                }
                
            } catch (error) {
                console.error('Error inserting gallery item:', error);
                this.show_notification_modal(this.get_t('gallery.insert_error'), 'error');
            }
        },
        
        /**
         * Show gallery modal
         */
        async show_gallery() {
            this.show_gallery_modal = true;
            
            // Load categories first, then load items
            await this.load_gallery_categories();
            
            // Only load items if we have valid tab and subtab
            if (this.active_gallery_tab && this.active_gallery_subtab) {
                this.load_gallery_items(this.active_gallery_tab, this.active_gallery_subtab);
            }
        },
        
        /**
         * Hide gallery modal
         */
        hide_gallery() {
            this.show_gallery_modal = false;
            // Clean up sortable when hiding gallery
            this.destroy_gallery_sortable();
        },
        
        /**
         * Switch gallery tab
         */
        switch_gallery_tab(tab_id) {
            // Ensure the tab_id is valid category
            if (!this.gallery_categories[tab_id]) {
                console.error('Invalid gallery tab:', tab_id);
                return;
            }
            
            this.active_gallery_tab = tab_id;
            // Reset to first subcategory when switching main category
            const category_data = this.gallery_categories[tab_id];
            if (category_data && category_data.subcategories && category_data.subcategories.length > 0) {
                this.active_gallery_subtab = category_data.subcategories[0].clean;
            }
            
            // Load items for new category and first subcategory
            this.load_gallery_items(tab_id, this.active_gallery_subtab);
        },
        
        /**
         * Switch gallery subtab (subcategory)
         */
        switch_gallery_subtab(subcategory) {
            this.active_gallery_subtab = subcategory;
            this.load_gallery_items(this.active_gallery_tab, subcategory);
        },
        
        /**
         * Show admin panel (only in dev environment)
         */
        show_admin_gallery_panel() {
            // Only show in dev environment
            if (this.$config && this.$config.env === 'dev') {
                this.show_admin_panel = true;
            }
        },
        
        /**
         * Add selected object to gallery from admin interface
         */
        async add_selected_to_gallery() {
            if (!this.admin_element_name.trim()) {
                this.show_notification_modal(this.get_t('gallery.element_name_required'), 'warning');
                return;
            }

            await this.add_to_gallery(this.admin_selected_category, this.admin_selected_subcategory, this.admin_element_name.trim());
            
            // Clear the form after successful addition
            this.admin_element_name = '';
            // Keep premium setting as it is for next element
        },

        /**
         * Toggle gallery admin mode (only in dev environment)
         */
        toggle_gallery_admin_mode() {
            if (this.is_dev_environment) {
                this.gallery_admin_mode = !this.gallery_admin_mode;
            }
        },

        /**
         * Update item creation date (for reordering)
         */
        async update_item_date(item, new_date) {
            if (!this.is_dev_environment) return;
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'update_gallery_item_date');
                form_data.append('item_id', item.id);
                form_data.append('new_date', new_date);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    // Reload gallery items to show new order
                    this.load_gallery_items(this.active_gallery_tab, this.active_gallery_subtab);
                    this.show_notification_modal(this.get_t('gallery.item_updated'), 'success');
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error updating item date:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Move item to top (newest)
         */
        async move_item_to_top(item) {
            const new_date = new Date().toISOString();
            await this.update_item_date(item, new_date);
        },

        /**
         * Toggle item premium status
         */
        async toggle_item_premium(item) {
            if (!this.is_dev_environment) return;
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'toggle_gallery_item_premium');
                form_data.append('item_id', item.id);
                form_data.append('is_premium', item.is_premium ? 'false' : 'true');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    // Update item locally
                    item.is_premium = !item.is_premium;
                    this.show_notification_modal(this.get_t('gallery.item_updated'), 'success');
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error toggling premium status:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Delete gallery item
         */
        async delete_gallery_item(item) {
            if (!this.is_dev_environment) return;
            
            if (!confirm(this.get_t('gallery.confirm_delete').replace('{name}', item.name))) {
                return;
            }
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'delete_gallery_item');
                form_data.append('item_id', item.id);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    // Reload gallery items to reflect deletion
                    this.load_gallery_items(this.active_gallery_tab, this.active_gallery_subtab);
                    this.show_notification_modal(this.get_t('gallery.item_deleted'), 'success');
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error deleting item:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Edit item name
         */
        async edit_item_name(item) {
            if (!this.is_dev_environment) return;
            
            const new_name = prompt(this.get_t('gallery.enter_new_name'), item.name);
            if (!new_name || new_name.trim() === '' || new_name.trim() === item.name) {
                return;
            }
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'update_gallery_item_name');
                form_data.append('item_id', item.id);
                form_data.append('new_name', new_name.trim());
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.success) {
                    // Update item locally
                    item.name = new_name.trim();
                    this.show_notification_modal(this.get_t('gallery.item_updated'), 'success');
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error updating item name:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Show item details (filename, etc.)
         */
        show_item_details(item) {
            if (!this.is_dev_environment) return;
            
            const details = [
                `${this.get_t('gallery.item_name')}: ${item.name}`,
                `${this.get_t('gallery.file_name')}: ${item.filename || item.id + '.mkc'}`,
                `${this.get_t('gallery.item_id')}: ${item.id}`,
                `${this.get_t('gallery.created_date')}: ${new Date(item.created_at).toLocaleString()}`,
                `${this.get_t('gallery.premium_status')}: ${item.is_premium ? this.get_t('gallery.premium') : this.get_t('gallery.free')}`
            ].join('\n\n');
            
            this.show_notification_modal(details, 'info');
        },

        async check_dev_environment() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'check_dev_environment');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                this.is_dev_environment = response.data.is_dev || false;
            } catch (error) {
                console.error('Error checking dev environment:', error);
                this.is_dev_environment = false;
            }
        },

        /**
         * Reorder gallery items using drag & drop order
         */
        async reorder_gallery_items(items_order) {
            if (!this.is_dev_environment) return;
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'reorder_gallery_items');
                form_data.append('category', this.active_gallery_tab);
                form_data.append('subcategory', this.active_gallery_subtab);
                form_data.append('items_order', JSON.stringify(items_order));
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data && response.data.success) {
                    // Reload gallery items to show new order
                    await this.load_gallery_items(this.active_gallery_tab, this.active_gallery_subtab);
                    this.show_notification_modal(this.get_t('gallery.items_reordered'), 'success');
                } else {
                    this.show_notification_modal(this.get_t('gallery.error'), 'error');
                }
            } catch (error) {
                console.error('Error reordering items:', error);
                this.show_notification_modal(this.get_t('gallery.error'), 'error');
            }
        },

        /**
         * Initialize drag & drop sorting for gallery items
         */
        init_gallery_sortable() {
            this.destroy_gallery_sortable(); // Clean up first
            
            const gallery_grid = document.querySelector('.sortable-gallery');
            if (!gallery_grid || !window.Sortable) {
                return;
            }

            this.gallery_sortable = Sortable.create(gallery_grid, {
                animation: 200,
                ghostClass: 'gallery-item-ghost',
                chosenClass: 'gallery-item-chosen',
                dragClass: 'gallery-item-drag',
                
                onStart: (evt) => {
                    // Add dragging class for visual feedback
                    document.body.classList.add('gallery-dragging');
                },
                
                onEnd: (evt) => {
                    // Remove dragging class
                    document.body.classList.remove('gallery-dragging');
                    
                    // Check if position actually changed
                    if (evt.oldIndex !== evt.newIndex) {
                        // Get current items in their display order
                        const current_items = [...this.current_gallery_items];
                        const moved_item = current_items[evt.oldIndex];
                        
                        // Validate that we're only reordering within the same group (free/premium)
                        const target_item = current_items[evt.newIndex];
                        if (moved_item.is_premium !== target_item.is_premium) {
                            this.show_notification_modal(this.get_t('gallery.cannot_reorder_between_groups'), 'error');
                            return;
                        }
                        
                        // Reorder the array based on the drag & drop
                        current_items.splice(evt.oldIndex, 1);
                        current_items.splice(evt.newIndex, 0, moved_item);
                        
                        // Recalculate order_index for the affected group only
                        const is_premium_group = moved_item.is_premium;
                        const group_items = current_items.filter(item => item.is_premium === is_premium_group);
                        
                        // Create new order with sequential indices (1-based) for the affected group
                        const items_order = group_items.map((item, index) => ({
                            id: item.id,
                            order_index: index + 1
                        }));
                        
                        // Send reorder request to server
                        this.reorder_gallery_items(items_order);
                    }
                }
            });
        },

        /**
         * Destroy gallery sortable instance
         */
        destroy_gallery_sortable() {
            if (this.gallery_sortable) {
                this.gallery_sortable.destroy();
                this.gallery_sortable = null;
            }
        },
    }
};